import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import { BookingEvent } from './../interfaces/booking-event';
import {
  actionsFactory,
  gettersFactory,
  mutationsFactory,
  stateFactory,
} from '@shared/store/abstract-form.store';
import { Resource } from '@centers/interfaces/resource';
import { Booking } from '@bookings/interfaces/booking';

import { Discipline } from '@centers/interfaces/discipline';
import { Contact } from '@contacts/interfaces/contact';
import BPCMessageService from '@core/services/bpc-messages.service';
import { PriceCategory } from '@contacts/interfaces/price-category';
import { Article } from '@accounting/interfaces/article';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import AuthorizationService from '@auth/services/authorization.service';
import _ from 'lodash';
import { MultiplayerBooking } from '@bookings/interfaces/multiplayer-booking';

const bpcRouterHelper = new BPCApiRouteHelper();
const bpcMessage = new BPCMessageService();

// NOTE Do not import like that it seems that then they are constants and the state is shared
// import { state, getters, actions, mutations } from '@shared/store/abstract-form.store';

//  We can extends abstract state getters actions and muttations
//  For example:
const abstract_state = stateFactory<BookingEvent>(new BookingEvent());
const abstract_getters = gettersFactory<BookingEvent>();
const abstract_actions = actionsFactory<BookingEvent>();
const abstract_mutations = mutationsFactory<BookingEvent>(new BookingEvent());
const $authorizationService = new AuthorizationService();

const state = {
  ...abstract_state,
  showReplaceButton: false,
  loadingConflicts: false,
  showConflicts: false,
  checkingPrice: false,
  bookingEventFormModelChanged: false,
  newPrice: null,
  originalPrice: null,
  newForcedPrice: null,
  defaultUpdateOrdersValue: null,
  noRefund: null,

  // extends with new prop and methods here
};

const getters = {
  ...abstract_getters,
  // extends with new prop and methods here

  resource: (state): Resource => {
    return state.model?.resource || null;
  },
  booking: (state): Booking => {
    return state.model?.booking || null;
  },
  contact: (state): Contact => {
    return state.model?.contact || null;
  },
  discipline: (state): Discipline => {
    return state.model?.discipline || null;
  },
  model: (state): BookingEvent => {
    return state.model || null;
  },
  date: (state): string => {
    return state.model?.date || null;
  },
  startTime: (state): string => {
    return state.model?.start_time || null;
  },
  endTime: (state): string => {
    return state.model?.end_time || null;
  },
  resourceId: (state, getters): number => {
    return getters.resource?.id ? Number(getters.resource?.id) : null;
  },
  showConflicts: (state): boolean => {
    return state.showConflicts;
  },
  checkingPrice: (state): boolean => {
    return state.checkingPrice;
  },
  bookingId: (state, getters): number => {
    return state.model?.booking_id
      ? Number(state.model.booking_id)
      : state.model?.booking?.id
      ? Number(state.model.booking.id)
      : null;
  },
  contactId: (state, getters): number => {
    return getters.contact?.id ? Number(getters.contact?.id) : null;
  },
  contactPriceCategory: (state, getters): number => {
    return getters?.contact?.price_category;
  },
  contactPriceCategoryId: (state, getters): number => {
    return getters.contactPriceCategory?.id ? Number(getters.contactPriceCategory?.id) : null;
  },

  priceCategory: (state): PriceCategory => {
    return state.model?.price_category;
  },
  priceCategoryId: (state, getters): number => {
    return getters.priceCategory?.id ? Number(getters.priceCategory?.id) : null;
  },
  disciplineId: (state, getters): number => {
    return getters.discipline?.id ? Number(getters.discipline?.id) : null;
  },
  showReplaceButton: (state, getters): boolean => {
    return (state.showReplaceButton = !$authorizationService.hasPermission('replace_bookings')
      ? false
      : state.showReplaceButton && getters.showConflicts);
  },
  showSaveButton: (state, getters): boolean => {
    return !getters.showReplaceButton;
  },
  saveButtonDisabled: (state, getters): boolean => {
    return (
      (!getters.bookingId && getters.mode === 'api') ||
      !getters.date ||
      !getters.startTime ||
      !getters.endTime ||
      (!getters.contactId && getters.bookingType === 'booking') ||
      getters.checkingPrice
    );
  },
  loadingConflicts: (state): boolean => {
    return state.loadingConflicts;
  },
  showSavingSpinner: (state, getters): boolean => {
    return getters.loadingConflicts || getters.isSaving;
  },

  replaceButtonDisabled: (state, getters): boolean => {
    return (
      (!getters.bookingId && getters.mode === 'api') ||
      !getters.date ||
      !getters.startTime ||
      !getters.endTime ||
      !getters.contactId
    );
  },

  profileCanChangeOrders: (state, getters): boolean => {
    return $authorizationService.hasPermission('force_price_update_booking');
  },

  defaultUpdateOrdersValue: (state): string => {
    if (state.defaultUpdateOrdersValue) return 'update';
    else return 'no_update';
  },

  canGetPrice: (state, getters): boolean => {
    return (
      getters.date &&
      getters.startTime &&
      getters.endTime &&
      // getters.priceCategoryId &&
      getters.resource &&
      !Number.isInteger(getters.article)
    );
  },

  bookingEventFormModelChanged: (state): boolean => {
    return state.bookingEventFormModelChanged;
  },

  newPrice: (state): number => {
    return state.newPrice;
  },

  originalPrice: (state): number => {
    return state.originalPrice;
  },

  newForcedPrice: (state): number => {
    return state.newForcedPrice;
  },

  noRefund: (state): boolean => {
    return state.noRefund;
  },

  selectedSuggestedArticles: (state): object => {
    return state.model?.selected_suggested_articles || null;
  },

  reductionArticles: (state): [] => {
    return state.model?.reductionArticles || null;
  },

  orders: (state): [] => {
    return state.model?.orders || null;
  },

  status: (state): string => {
    return state.model?.status || null;
  },

  repetitive: (state): boolean => {
    return state.model?.own_to_repetitive_booking === 1 || state.model?.own_to_repetitive_booking
      ? true
      : false;
  },
  bookingType: (state): boolean => {
    return state.model?.booking_type || null;
  },
  article: (state): Article => {
    return state.model?.article;
  },
  articleId: (state, getters): number => {
    return getters.article?.id ? Number(getters.article?.id) : null;
  },
  isMultiplayer: (state): boolean => {
    console.log('is multiplayer : ' + state.model?.is_multiplayer);
    return !!state.model?.is_multiplayer;
  },
  multiplayerData: (state): MultiplayerBooking => {
    return state.model?.multiplayer_data;
  },
};
const actions = {
  ...abstract_actions,
  resetForm: async ({ commit, getters }): Promise<void> => {
    commit('setInitialState');
    commit('setBookingEventInitialState');
    // TODO: Check why contact subscriptions field does not set the value in the formModel. Check if could happens with others fields
    commit('setInitialModel', { fields: getters.fieldsObject, model: null });
  },
  searchConflicts: async (
    { commit, getters },
    data: {
      date: string;
      start_time: string;
      end_time: string;
      booking_id: number;
      exclude_ids: number[];
    },
  ): Promise<BPCGenericApiResponse> => {
    console.log(commit, state, data);
    commit('setLoadingConflicts', true);
    const fetchRoute = `${bpcRouterHelper.getEntityPath('booking-event')}/conflicts`;
    const res = await bpcApi.post(fetchRoute, data).toPromise();
    commit('setLoadingConflicts', false);
    return res.data;
  },
  calcPrice: async ({ commit, getters }) => {
    const bookingEvent: BookingEvent = getters.model;

    if (_.get(bookingEvent, 'booking_type', null) !== 'label') {
      commit('setCheckingPrice', true);
      const basePath = bpcRouterHelper.getEntityPath('booking-event');
      // commit('setModelProp', { prop: 'booking_price', value: res['booking_price'] });
      try {
        const res = await bpcApi.post(basePath + '/get-price', bookingEvent).toPromise();
        commit('setNewPrice', res['event_price'].toFixed(2));
        commit('setOriginalPrice', res['original_event_price'].toFixed(2));
        commit('setNewForcedPrice', res['event_forced_price'].toFixed(2));
        commit('setDefaultUpdateOrdersValue', res['suggested_updated_orders']);
        commit('setNoRefund', res['no_refund']);
        commit('setCheckingPrice', false);
        return getters.newPrice;
      } catch (error) {
        commit('setCheckingPrice', false);
        bpcMessage.showErrorMessage({ text: 'global.generic.error_form_action' });
        throw error;
      }
    }
  },
  // extends with new prop and methods here
};
const mutations = {
  ...abstract_mutations,

  setBookingEventInitialState: (state): void => {
    // TODO implement initial state
    state.bookingEventFormModelChanged = false;
    state.checkingPrice = false;
    state.showReplaceButton = false;
    state.showConflicts = false;
    state.newPrice = null;
    state.originalPrice = null;
    state.newForcedPrice = null;
    state.noRefund = null;
    state.defaultUpdateOrdersValue = null;
  },

  setShowReplaceButton: (state, value: boolean): void => {
    state.showReplaceButton = value;
  },
  setShowConflicts: (state, value: boolean): void => {
    state.showConflicts = value;
  },
  setLoadingConflicts: (state, value: boolean): void => {
    state.loadingConflicts = value;
  },
  setCheckingPrice: (state, value: boolean) => {
    state.checkingPrice = value;
  },
  setNewPrice: (state, value: number) => {
    state.newPrice = value;
    state.model.event_price = value;
  },

  setOriginalPrice: (state, value: number) => {
    state.originalPrice = value;
    state.model.original_event_price = value;
  },

  setNewForcedPrice: (state, value: number) => {
    state.newForcedPrice = value;
  },

  setDefaultUpdateOrdersValue: (state, value: boolean) => {
    state.defaultUpdateOrdersValue = value;
  },

  setNoRefund: (state, value: boolean) => {
    state.noRefund = value;
  },
  setBookingEventFormModelChanged: (state, value: boolean) => {
    state.bookingEventFormModelChanged = value;
  },
  setPriceFromOrders: (state, value: number) => {
    state.model.event_price = value;
  },
  setActionForUpdate: (state, value: string) => {
    state.model.action_for_update = value;
  },
  setActionForConflicts: (state, value: string) => {
    state.model.action_for_conflicts = value;
  },
  setActionForOrders: (state, value: string) => {
    state.model.action_for_orders = value;
  },
  setNotifyByEmail: (state, value: boolean) => {
    state.model.notify_by_email = value;
  },
  // extends with new prop and methods here
};

export const BookingEventModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
